import dayjs from "./date";

// A calendar cell is a plain calendar day (a UTC day, since event times are displayed in UTC).
// react-datepicker hands back local-midnight Dates, so a clicked cell is read through its local
// Y/M/D fields and never converted through UTC.

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_DAY = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})$/;
const MONTH_NAMES = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

const isRealDay = (y, m, d) => {
    const probe = new Date(y, m - 1, d);
    return probe.getFullYear() === y && probe.getMonth() === m - 1 && probe.getDate() === d;
};

/** Clicked cell (local midnight) -> "YYYY-MM-DD" from its own fields. No UTC conversion. */
export const toCalendarDayISO = (date) => dayjs(date).format("YYYY-MM-DD");

/** "YYYY-MM-DD" -> local-midnight Date that renders as that cell. */
export const calendarDayToDate = (iso) => {
    const [y, m, d] = iso.split("-");
    return new Date(+y, +m - 1, +d);
};

export const getUtcToday = () => dayjs().utc().format("YYYY-MM-DD");

/** dayClassName for react-datepicker: marks the cell for UTC today (the "today" the lists use). */
export const utcTodayDayClassName = (date) =>
    toCalendarDayISO(date) === getUtcToday() ? "ssmu-utc-today" : undefined;

const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

/**
 * Which month a calendar should open to for the active selection: the most recent month that has
 * a selected day. That is the selection's last day when it ends today or earlier, today when the
 * selection spans today (e.g. "Next 30 days"), and the first selected day when the whole
 * selection is in the future. Opening on the range's first day would land on an older month.
 *
 * Returns the first day of that month, not the exact day: react-datepicker recenters whenever
 * `openToDate` changes to a different day, so returning the month start means the view only
 * resets when the month changes (not at UTC midnight or on a day click).
 */
export const getCalendarOpenToDate = (startDate, endDate) => {
    if (!startDate) return monthStart(utcDayCell(0));
    const end = endDate ?? startDate;
    const today = getUtcToday();
    if (toCalendarDayISO(end) <= today) return monthStart(end);
    if (toCalendarDayISO(startDate) <= today) return monthStart(utcDayCell(0));
    return monthStart(startDate);
};

/** Cell for UTC today +/- offsetDays. */
export const utcDayCell = (offsetDays = 0) =>
    calendarDayToDate(dayjs().utc().add(offsetDays, "day").format("YYYY-MM-DD"));

const monthNumber = (name) => {
    const lower = name.toLowerCase();
    const idx = MONTH_NAMES.findIndex((full) => full.startsWith(lower) || (lower === "sept" && full === "september"));
    return idx === -1 ? null : idx + 1;
};

/**
 * Parses a calendar day value into "YYYY-MM-DD", or null when the value is not a calendar day.
 *
 * Accepts "2026-10-21" and the legacy year-less label "October 21".
 * - Without `maxDay`: no clamping, and a legacy label uses the current year.
 * - With `maxDay`: later days clamp to `maxDay`, and a legacy label resolves to the most recent
 *   occurrence on or before `maxDay`.
 */
export const parseCalendarDay = (value, { maxDay } = {}) => {
    if (typeof value !== "string") return null;
    const text = value.trim();

    const iso = ISO_DAY.exec(text);
    if (iso) {
        const [, y, m, d] = iso;
        if (!isRealDay(+y, +m, +d)) return null;
        return maxDay && text > maxDay ? maxDay : text;
    }

    const label = MONTH_DAY.exec(text);
    if (!label) return null;
    const month = monthNumber(label[1]);
    const day = +label[2];
    if (!month) return null;

    if (!maxDay) {
        const year = dayjs().year();
        return isRealDay(year, month, day) ? toISO(year, month, day) : null;
    }

    // Walk back from maxDay's year; the extra years let Feb 29 land on a leap year.
    const startYear = +maxDay.slice(0, 4);
    for (let year = startYear; year >= startYear - 4; year--) {
        if (!isRealDay(year, month, day)) continue;
        const candidate = toISO(year, month, day);
        if (candidate <= maxDay) return candidate;
    }
    return null;
};
